// FILE: src/pages/DashboardPage.jsx
//
// Visual redesign sourced from a Claude Design handoff bundle (Dashboard.html).
// Functional contract preserved exactly from the previous implementation:
// every API call, state piece, drag-and-drop reorder + localStorage persistence,
// transaction-modal flow, currency handling, lock-error behaviour, and
// auto-refresh-after-save are identical.  Only the visual shell and styling
// have changed.
//
// One additive functional change: today's inflow/outflow is now shown on each
// account card. This required one extra /transactions call on initial load
// (with today's date filter) which is then aggregated per account. No new
// server endpoint and no schema change.
//
// All page styles live in PAGE_CSS, scoped under `.dashpg-root` so nothing
// leaks into other routes.

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  CircularProgress,
} from "@mui/material";
import API from "../api";
import { formatMoney } from "../utils/formatMoney";

// DnD Kit (kept from the original implementation for accessible reordering)
import {
  DndContext,
  closestCenter,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS as DndCSS } from "@dnd-kit/utilities";

// ────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────
const STORAGE_KEY = "accountOrder";
const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────
function fmtDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "";
  return `${String(d.getUTCDate()).padStart(2, "0")}-${
    MONTHS_SHORT[d.getUTCMonth()]
  }-${d.getUTCFullYear()}`;
}

function fmtLiveDate() {
  const d = new Date();
  return d.toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short", year: "numeric",
  });
}

/** Account status — drives the card's tinted background and left-rail colour. */
function statusOf(acc) {
  if (acc.notifications && Array.isArray(acc.notifications) && acc.notifications.length > 0) return "alert";
  if (acc.balance < 0) return "alert";
  if (acc.inReOpenMode) return "reopen";
  if (acc.lockedUpToDate) return "locked";
  return "healthy";
}

/** Number of accounts that need attention — used by the header chip. */
function alertCount(accounts) {
  return accounts.filter((a) =>
    (a.notifications && a.notifications.length > 0) || a.balance < 0
  ).length;
}

/** Split a balance into sign + whole + frac parts for stylable rendering. */
function balanceParts(balance) {
  const safe = Number.isFinite(balance) ? balance : 0;
  const abs = Math.abs(safe);
  const [whole, frac = "00"] = abs.toFixed(2).split(".");
  return {
    sign: safe < 0 ? "−" : "",
    whole: parseInt(whole, 10).toLocaleString("en-GB"),
    frac,
    tone: safe > 0 ? "pos" : safe < 0 ? "neg" : "zero",
  };
}

// ────────────────────────────────────────────────────────────────────
// Inline SVG icons (Feather-style, matching the design bundle)
// ────────────────────────────────────────────────────────────────────
function SvgIcon({ children, size = 16, sw = 1.6, fill = "none", stroke = "currentColor", style }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill={fill}
      stroke={stroke}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "inline-block", verticalAlign: "middle", flex: "none", ...style }}
    >
      {children}
    </svg>
  );
}

const ICONS = {
  drag: (
    <SvgIcon fill="currentColor" stroke="none">
      <circle cx="5" cy="3" r="1.3" /><circle cx="11" cy="3" r="1.3" />
      <circle cx="5" cy="8" r="1.3" /><circle cx="11" cy="8" r="1.3" />
      <circle cx="5" cy="13" r="1.3" /><circle cx="11" cy="13" r="1.3" />
    </SvgIcon>
  ),
  lock: (
    <SvgIcon>
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </SvgIcon>
  ),
  unlock: (
    <SvgIcon>
      <rect x="3" y="7" width="10" height="7" rx="1.5" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0" />
    </SvgIcon>
  ),
  alert: (
    <SvgIcon>
      <path d="M8 2.5L1.5 13.5h13L8 2.5z" />
      <path d="M8 6.5v3" />
      <circle cx="8" cy="11.5" r=".6" fill="currentColor" />
    </SvgIcon>
  ),
  close: (
    <SvgIcon sw={1.7}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </SvgIcon>
  ),
  arrowDown: (
    <SvgIcon sw={1.7}>
      <path d="M8 3v10M4 9l4 4 4-4" />
    </SvgIcon>
  ),
  arrowUp: (
    <SvgIcon sw={1.7}>
      <path d="M8 13V3M4 7l4-4 4 4" />
    </SvgIcon>
  ),
  coin: (
    <SvgIcon>
      <circle cx="8" cy="8" r="5.5" />
      <path d="M8 5v6M6.5 6.5h2.25a1.25 1.25 0 010 2.5H7.25a1.25 1.25 0 000 2.5H9.5" />
    </SvgIcon>
  ),
  card: (
    <SvgIcon>
      <rect x="1.75" y="3.5" width="12.5" height="9" rx="1.5" />
      <path d="M1.75 6.5h12.5M3.75 10h2.5" />
    </SvgIcon>
  ),
  swap: (
    <SvgIcon>
      <path d="M2.5 5.5h9l-2.25-2.5M13.5 10.5h-9l2.25 2.5" />
    </SvgIcon>
  ),
  list: (
    <SvgIcon>
      <path d="M3 4.5h10M3 8h10M3 11.5h6" />
    </SvgIcon>
  ),
  paperclip: (
    <SvgIcon>
      <path d="M11.5 7L7 11.5a2.5 2.5 0 11-3.5-3.5l5.5-5.5a1.75 1.75 0 112.5 2.5L6 10.5a1 1 0 11-1.5-1.5L9 4.5" />
    </SvgIcon>
  ),
  check: (
    <SvgIcon sw={1.7}>
      <path d="M3 8.5L6.5 12 13 5" />
    </SvgIcon>
  ),
  pulse: <span className="dashpg-pulse" aria-hidden="true" />,
};

// ────────────────────────────────────────────────────────────────────
// Scoped page CSS (sourced from Dashboard.html, prefixed with .dashpg-root)
// ────────────────────────────────────────────────────────────────────
const PAGE_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&display=swap');

.dashpg-root {
  --white: #FFFFFF;
  --bg: #FFFFFF;
  --ink: #0B0C0E;
  --ink-2: #2A2D33;
  --ink-3: #5B6068;
  --ink-4: #9499A1;
  --line: #ECECEF;
  --line-strong: #DEDFE3;
  --hover: #F7F7F9;
  --sun: #FFC107;
  --sun-deep: #A77A00;
  --sun-soft: #FFF5D1;
  --azure: #1E90FF;
  --azure-soft: #E8F2FF;
  --green: #2E7D32;
  --green-soft: #E8F4E8;
  --red: #C62828;
  --red-soft: #FCEDED;
  --orange: #ED6C02;
  --orange-soft: #FFEFDE;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: var(--ink);
  background: var(--bg);
  -webkit-font-smoothing: antialiased;
  font-feature-settings: 'cv11', 'ss01';
  min-height: 100vh;
}
.dashpg-root *, .dashpg-root *::before, .dashpg-root *::after { box-sizing: border-box; }
.dashpg-root .mono { font-family: 'Geist Mono', ui-monospace, monospace; font-feature-settings: 'tnum' on; }

/* ───── Page ───── */
.dashpg-root .dashpg-page {
  max-width: 1320px;
  margin: 0 auto;
  padding: 40px 32px 96px;
}
@media (max-width: 640px) {
  .dashpg-root .dashpg-page { padding: 24px 16px 64px; }
}

.dashpg-root .dashpg-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 32px;
  margin-bottom: 48px;
  padding-bottom: 24px;
  flex-wrap: wrap;
}

.dashpg-root .dashpg-eyebrow {
  font-family: 'Geist Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-4);
  margin-bottom: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.dashpg-root .dashpg-pulse {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--green);
  box-shadow: 0 0 0 4px rgba(46,125,50,0.12);
  display: inline-block;
}

.dashpg-root .dashpg-title {
  font-size: clamp(40px, 5.4vw, 64px);
  font-weight: 700;
  margin: 0;
  line-height: 0.96;
  letter-spacing: -0.04em;
  color: var(--ink);
}

.dashpg-root .dashpg-sub {
  margin: 14px 0 0;
  font-size: 14.5px;
  color: var(--ink-3);
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.dashpg-root .dashpg-sub strong { color: var(--ink); font-weight: 600; }
.dashpg-root .dashpg-sub .dot { width: 3px; height: 3px; border-radius: 50%; background: var(--ink-4); }

.dashpg-root .dashpg-meta {
  display: flex; flex-direction: column; gap: 8px; align-items: flex-end;
  font-size: 13px; color: var(--ink-3);
}
.dashpg-root .dashpg-meta-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 6px 12px;
  border: 1px solid var(--line);
  border-radius: 999px;
  font-size: 12.5px;
  color: var(--ink-2);
  background: var(--white);
  font-weight: 500;
}
.dashpg-root .dashpg-meta-chip svg { width: 12px; height: 12px; }
.dashpg-root .dashpg-meta-chip-warn { background: var(--sun-soft); border-color: #F0D88E; color: var(--sun-deep); }

/* ───── Grid ───── */
.dashpg-root .dashpg-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px 32px;
}
@media (max-width: 980px) { .dashpg-root .dashpg-grid { grid-template-columns: repeat(2, 1fr); gap: 24px; } }
@media (max-width: 640px) { .dashpg-root .dashpg-grid { grid-template-columns: 1fr; gap: 18px; } }

/* ───── Card ───── */
.dashpg-root .dashpg-card {
  background: var(--card-bg, #F7F6F2);
  border: 1px solid var(--card-border, var(--line));
  border-radius: 18px;
  padding: 24px 22px 20px 28px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  min-height: 280px;
  position: relative;
  transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
  overflow: hidden;
}
.dashpg-root .dashpg-card::before {
  content: '';
  position: absolute;
  left: 0; top: 18px; bottom: 18px;
  width: 4px;
  background: var(--card-accent, var(--ink));
  border-radius: 0 4px 4px 0;
  transition: top .2s, bottom .2s;
}
.dashpg-root .dashpg-card[data-status="healthy"] { --card-bg: #F4F1E8; --card-border: #E5E0D0; --card-accent: var(--ink); }
.dashpg-root .dashpg-card[data-status="locked"]  { --card-bg: #FFF4D1; --card-border: #F0D88E; --card-accent: var(--sun-deep); }
.dashpg-root .dashpg-card[data-status="reopen"]  { --card-bg: #E0F0DD; --card-border: #B5D2AE; --card-accent: var(--green); }
.dashpg-root .dashpg-card[data-status="alert"]   { --card-bg: #FCE2DC; --card-border: #ECC2C2; --card-accent: var(--red); }

.dashpg-root .dashpg-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 14px 32px rgba(11,12,14,0.10), 0 2px 4px rgba(11,12,14,0.04);
}
.dashpg-root .dashpg-card:hover::before { top: 12px; bottom: 12px; }
.dashpg-root .dashpg-card:focus-visible { outline: 2px solid var(--ink); outline-offset: 3px; }
.dashpg-root .dashpg-card-dragging {
  opacity: 0.85;
  transform: rotate(-1.5deg) scale(1.02);
  box-shadow: 0 24px 44px rgba(11,12,14,0.2);
  cursor: grabbing;
  z-index: 5;
}
.dashpg-root .dashpg-card-archived { filter: grayscale(70%); opacity: .65; }

.dashpg-root .dashpg-card-top {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 10px; margin-bottom: 4px;
}
.dashpg-root .dashpg-card-name-block { min-width: 0; flex: 1; }
.dashpg-root .dashpg-card-name {
  font-size: 24px;
  font-weight: 700;
  letter-spacing: -0.024em;
  line-height: 1.15;
  color: var(--ink);
  margin: 0 0 6px;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  -webkit-box-orient: vertical;
}
.dashpg-root .dashpg-card-num {
  font-family: 'Geist Mono', monospace;
  font-size: 11.5px;
  font-weight: 500;
  color: var(--ink-2);
  letter-spacing: 0.02em;
  background: rgba(255,255,255,0.55);
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
  border: 1px solid rgba(11,12,14,0.06);
}
.dashpg-root .dashpg-card-drag {
  width: 26px; height: 26px;
  display: grid; place-items: center;
  color: var(--ink-4);
  cursor: grab;
  flex-shrink: 0;
  opacity: 0.35;
  transition: opacity .15s, color .15s, background .15s;
  border-radius: 6px;
  margin: -2px -4px 0 0;
  background: transparent;
  border: 0;
  touch-action: none;
}
.dashpg-root .dashpg-card:hover .dashpg-card-drag { opacity: 1; }
.dashpg-root .dashpg-card-drag:hover { color: var(--ink); background: var(--hover); }
.dashpg-root .dashpg-card-drag:active { cursor: grabbing; }
.dashpg-root .dashpg-card-drag svg { width: 15px; height: 15px; }

.dashpg-root .dashpg-balance-label {
  font-family: 'Geist Mono', monospace;
  font-size: 10.5px;
  font-weight: 500;
  color: var(--ink-4);
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-top: 14px;
  margin-bottom: 2px;
}
.dashpg-root .dashpg-balance {
  margin: 0;
  font-size: 44px;
  font-weight: 700;
  letter-spacing: -0.032em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: baseline;
  gap: 1px;
  word-break: break-all;
}
.dashpg-root .dashpg-balance .ccy {
  font-size: 24px;
  font-weight: 500;
  opacity: 0.45;
  margin-right: 4px;
}
.dashpg-root .dashpg-balance .frac { font-size: 26px; opacity: 0.4; font-weight: 600; }
.dashpg-root .dashpg-balance-pos { color: var(--ink); }
.dashpg-root .dashpg-balance-neg { color: var(--red); }
.dashpg-root .dashpg-balance-neg .ccy { color: var(--red); opacity: 0.6; }
.dashpg-root .dashpg-balance-zero { color: var(--ink-4); }
.dashpg-root .dashpg-balance-zero .ccy { color: var(--ink-4); opacity: 0.6; }

.dashpg-root .dashpg-flow {
  display: grid;
  grid-template-columns: 1fr 1px 1fr;
  gap: 12px;
  align-items: center;
  margin-top: 18px;
  padding: 12px 14px;
  background: rgba(255,255,255,0.6);
  border: 1px solid rgba(11,12,14,0.05);
  border-radius: 10px;
}
.dashpg-root .dashpg-flow-cell { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.dashpg-root .dashpg-flow-label {
  font-family: 'Geist Mono', monospace;
  font-size: 9.5px;
  font-weight: 500;
  color: var(--ink-3);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  display: flex; align-items: center; gap: 5px;
}
.dashpg-root .dashpg-flow-label svg { width: 9px; height: 9px; }
.dashpg-root .dashpg-flow-cell-in .dashpg-flow-label { color: var(--green); }
.dashpg-root .dashpg-flow-cell-out .dashpg-flow-label { color: var(--red); }
.dashpg-root .dashpg-flow-val {
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
  color: var(--ink);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dashpg-root .dashpg-flow-val.is-zero { color: var(--ink-4); font-weight: 500; }
.dashpg-root .dashpg-flow-divider { width: 1px; height: 28px; background: rgba(11,12,14,0.08); }

.dashpg-root .dashpg-card-desc {
  font-size: 13px;
  color: var(--ink-3);
  line-height: 1.5;
  margin: 16px 0 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.dashpg-root .dashpg-card-spacer { flex: 1; min-height: 12px; }

.dashpg-root .dashpg-card-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed rgba(11,12,14,0.12);
}
.dashpg-root .dashpg-card-chips:empty { display: none; }

/* chips */
.dashpg-root .dashpg-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  padding: 3px 10px 3px 9px;
  border-radius: 999px;
  line-height: 1.4;
  white-space: nowrap;
  border: 1px solid transparent;
  letter-spacing: 0.005em;
}
.dashpg-root .dashpg-chip svg { width: 10px; height: 10px; flex-shrink: 0; }
.dashpg-root .dashpg-chip-locked {
  background: var(--sun);
  color: #2A1F00;
  border-color: #D9A800;
}
.dashpg-root .dashpg-chip-reopen {
  background: var(--green);
  color: #FFFFFF;
  border-color: var(--green);
}
.dashpg-root .dashpg-chip-alert {
  background: var(--red);
  color: #FFFFFF;
  border-color: var(--red);
}

/* ───── State containers (loading / empty) ───── */
.dashpg-root .dashpg-state {
  min-height: 320px;
  display: flex; align-items: center; justify-content: center; flex-direction: column;
  gap: 14px;
  color: var(--ink-3);
  font-size: 14px;
  text-align: center;
}
.dashpg-root .dashpg-spinner-lg {
  width: 30px; height: 30px;
  border: 3px solid var(--line);
  border-top-color: var(--ink);
  border-radius: 50%;
  animation: dashpg-spin 1s linear infinite;
}
@keyframes dashpg-spin { to { transform: rotate(360deg); } }
.dashpg-root .dashpg-state-illu {
  width: 56px; height: 56px; border-radius: 14px;
  background: var(--sun-soft); color: var(--sun-deep);
  display: grid; place-items: center; margin-bottom: 4px;
  border: 1px solid #F0D88E;
}
.dashpg-root .dashpg-state-title {
  font-size: 18px; font-weight: 600; color: var(--ink);
}
.dashpg-root .dashpg-state-text { max-width: 360px; }

/* ───── Modal ───── */
.dashpg-modal-root {
  font-family: 'Geist', -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #0B0C0E;
  -webkit-font-smoothing: antialiased;
  font-feature-settings: 'cv11', 'ss01';
}
.dashpg-modal-root *, .dashpg-modal-root *::before, .dashpg-modal-root *::after { box-sizing: border-box; }

.dashpg-modal-head {
  padding: 22px 24px 18px;
  border-bottom: 1px solid #ECECEF;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  background: #FFFFFF;
}
.dashpg-modal-eyebrow {
  font-family: 'Geist Mono', monospace;
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.16em;
  color: #9499A1;
  margin-bottom: 8px;
  font-weight: 500;
}
.dashpg-modal-title {
  font-size: 22px;
  font-weight: 600;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: #0B0C0E;
}
.dashpg-modal-sub {
  font-family: 'Geist Mono', monospace;
  font-size: 12px;
  color: #5B6068;
  margin: 6px 0 0;
  font-variant-numeric: tabular-nums;
}
.dashpg-modal-close {
  width: 32px; height: 32px; border-radius: 8px;
  border: 1px solid #ECECEF; background: #FFFFFF; color: #2A2D33;
  display: grid; place-items: center;
  transition: background .12s, color .12s;
  cursor: pointer;
  font-family: inherit;
}
.dashpg-modal-close:hover { background: #F7F7F9; color: #0B0C0E; }
.dashpg-modal-close svg { width: 14px; height: 14px; }
.dashpg-modal-body { padding: 20px 24px 24px; }

.dashpg-stats {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  margin-bottom: 20px;
}
@media (max-width: 540px) {
  .dashpg-stats { grid-template-columns: 1fr; }
}
.dashpg-stat {
  padding: 14px 16px;
  display: flex; flex-direction: column; gap: 4px;
  border-radius: 12px;
  border: 1px solid transparent;
}
.dashpg-stat-in  { background: #E8F4E8; border-color: #C8E1CA; }
.dashpg-stat-out { background: #FCEDED; border-color: #ECC2C2; }
.dashpg-stat-label {
  font-family: 'Geist Mono', monospace;
  font-size: 10.5px; font-weight: 500;
  text-transform: uppercase; letter-spacing: 0.12em;
  display: flex; align-items: center; gap: 6px;
}
.dashpg-stat-label svg { width: 12px; height: 12px; flex-shrink: 0; }
.dashpg-stat-in .dashpg-stat-label { color: #2E7D32; }
.dashpg-stat-out .dashpg-stat-label { color: #C62828; }
.dashpg-stat-val {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.dashpg-stat-in .dashpg-stat-val { color: #2E7D32; }
.dashpg-stat-out .dashpg-stat-val { color: #C62828; }

.dashpg-types {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin: 0 0 18px;
  padding: 4px;
  background: #F7F7F9;
  border-radius: 10px;
}
@media (max-width: 520px) {
  .dashpg-types { grid-template-columns: 1fr 1fr; }
}
.dashpg-type-btn {
  border: none;
  background: transparent;
  padding: 10px 8px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 500;
  color: #2A2D33;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: all .15s;
  cursor: pointer;
  font-family: inherit;
}
.dashpg-type-btn svg { width: 14px; height: 14px; }
.dashpg-type-btn:hover { background: #FFFFFF; color: #0B0C0E; }
.dashpg-type-btn.is-active { font-weight: 600; }
.dashpg-type-btn[data-type="receipt"].is-active  { background: #2E7D32; color: #FFFFFF; }
.dashpg-type-btn[data-type="payment"].is-active  { background: #C62828; color: #FFFFFF; }
.dashpg-type-btn[data-type="transfer"].is-active { background: #ED6C02; color: #FFFFFF; }
.dashpg-type-btn[data-type="report"] {
  background: #FFFFFF;
  color: #1E90FF;
  border: 1px solid #E8F2FF;
}
.dashpg-type-btn[data-type="report"]:hover { background: #1E90FF; color: #FFFFFF; border-color: #1E90FF; }

.dashpg-field { display: block; margin-bottom: 14px; }
.dashpg-field-label {
  font-family: 'Geist Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: #5B6068;
  display: flex; justify-content: space-between; margin-bottom: 6px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.dashpg-field-label .opt { color: #9499A1; font-weight: 500; }

.dashpg-modal-input,
.dashpg-modal-select {
  width: 100%;
  border: 1px solid #ECECEF;
  background: #FFFFFF;
  border-radius: 10px;
  padding: 11px 12px;
  font-size: 14px;
  color: #0B0C0E;
  transition: border-color .15s, box-shadow .15s;
  outline: none;
  font-family: inherit;
}
.dashpg-modal-input:focus,
.dashpg-modal-select:focus {
  border-color: #0B0C0E;
  box-shadow: 0 0 0 3px #F7F7F9;
}

.dashpg-amount-wrap { position: relative; }
.dashpg-amount-wrap .ccy {
  position: absolute; left: 14px; top: 50%; transform: translateY(-50%);
  font-size: 16px;
  font-weight: 500;
  color: #5B6068;
  pointer-events: none;
}
.dashpg-amount-input {
  padding-left: 28px;
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  font-size: 16px;
}

.dashpg-upload {
  display: flex; align-items: center; gap: 10px;
  padding: 11px 13px;
  border: 1px dashed #DEDFE3;
  border-radius: 10px;
  color: #5B6068;
  font-size: 13px;
  background: #FFFFFF;
  cursor: pointer;
  font-weight: 500;
  transition: background .15s, border-color .15s, color .15s;
}
.dashpg-upload:hover { background: #F7F7F9; border-color: #5B6068; color: #0B0C0E; }
.dashpg-upload svg { width: 14px; height: 14px; }
.dashpg-upload input { display: none; }
.dashpg-upload-file {
  font-size: 12px;
  color: #2A2D33;
  margin-top: 6px;
  display: flex; align-items: center; gap: 6px;
  font-family: 'Geist Mono', monospace;
}
.dashpg-upload-file svg { width: 12px; height: 12px; color: #2E7D32; flex-shrink: 0; }

.dashpg-save-btn {
  width: 100%;
  padding: 13px;
  border-radius: 10px;
  border: none;
  color: #FFFFFF;
  font-size: 14px;
  font-weight: 600;
  margin-top: 6px;
  display: flex; align-items: center; justify-content: center;
  gap: 8px;
  transition: filter .12s, transform .05s;
  cursor: pointer;
  font-family: inherit;
}
.dashpg-save-btn:hover:not(:disabled) { filter: brightness(.95); }
.dashpg-save-btn:active:not(:disabled) { transform: scale(0.99); }
.dashpg-save-btn:disabled { opacity: 0.7; cursor: not-allowed; }
.dashpg-save-btn[data-type="receipt"]  { background: #2E7D32; }
.dashpg-save-btn[data-type="payment"]  { background: #C62828; }
.dashpg-save-btn[data-type="transfer"] { background: #ED6C02; }

.dashpg-modal-spinner {
  width: 14px; height: 14px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.35);
  border-top-color: #FFFFFF;
  animation: dashpg-spin .7s linear infinite;
}

.dashpg-alert {
  padding: 11px 13px;
  border-radius: 10px;
  font-size: 13px;
  margin: 0 0 16px;
  display: flex; align-items: flex-start; gap: 8px;
  line-height: 1.4;
  border: 1px solid;
  font-weight: 500;
}
.dashpg-alert svg { width: 14px; height: 14px; flex-shrink: 0; margin-top: 2px; }
.dashpg-alert-ok   { background: #E8F4E8; color: #2E7D32; border-color: #C8E1CA; }
.dashpg-alert-err  { background: #FCEDED; color: #C62828; border-color: #ECC2C2; }
.dashpg-alert-lock { background: #FFF5D1; color: #A77A00; border-color: #F0D88E; }
`;

// ────────────────────────────────────────────────────────────────────
// Sub-components
// ────────────────────────────────────────────────────────────────────
function StatusChips({ acc }) {
  const chips = [];
  if (acc.lockedUpToDate) {
    chips.push(
      <span key="lock" className="dashpg-chip dashpg-chip-locked">
        {ICONS.lock}
        Locked until {fmtDate(acc.lockedUpToDate)}
      </span>
    );
  }
  if (acc.inReOpenMode) {
    chips.push(
      <span key="reopen" className="dashpg-chip dashpg-chip-reopen">
        {ICONS.unlock}
        ReOpen Mode
      </span>
    );
  }
  if (acc.notifications && Array.isArray(acc.notifications)) {
    acc.notifications.forEach((n, i) => {
      chips.push(
        <span key={`n${i}`} className="dashpg-chip dashpg-chip-alert">
          {ICONS.alert}
          {n}
        </span>
      );
    });
  }
  return chips;
}

function AccountCardInner({
  account,
  onClick,
  currency,
  isDragging,
  todayIn,
  todayOut,
  asOverlay,
}) {
  const status = statusOf(account);
  const bp = balanceParts(account?.balance || 0);
  const chips = StatusChips({ acc: account });
  const isArchived = !account?.isActive;
  const symbol = useMemo(() => {
    switch (currency) {
      case "USD": return "$";
      case "EUR": return "€";
      case "GBP": return "£";
      case "JPY": return "¥";
      case "BDT": return "৳";
      case "INR": return "₹";
      default: return currency || "";
    }
  }, [currency]);

  return (
    <div
      className={`dashpg-card ${isDragging || asOverlay ? "dashpg-card-dragging" : ""} ${
        isArchived ? "dashpg-card-archived" : ""
      }`}
      data-status={status}
      onClick={asOverlay ? undefined : onClick}
      tabIndex={asOverlay ? -1 : 0}
      role="button"
      aria-label={`Open actions for ${account?.accountName}`}
      onKeyDown={(e) => {
        if (asOverlay) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick && onClick();
        }
      }}
    >
      <div className="dashpg-card-top">
        <div className="dashpg-card-name-block">
          <h3 className="dashpg-card-name" title={account?.accountName}>
            {account?.accountName}
            {isArchived && " (Archived)"}
          </h3>
          <span className="dashpg-card-num">#{account?.accountNo}</span>
        </div>
        <span
          className="dashpg-card-drag"
          aria-hidden="true"
          title="Drag anywhere to reorder"
        >
          {ICONS.drag}
        </span>
      </div>

      <div className="dashpg-balance-label">Current balance</div>
      <div className={`dashpg-balance dashpg-balance-${bp.tone}`}>
        <span className="ccy">{symbol}</span>
        <span>
          {bp.sign}
          {bp.whole}
        </span>
        <span className="frac">.{bp.frac}</span>
      </div>

      <div className="dashpg-flow">
        <div className="dashpg-flow-cell dashpg-flow-cell-in">
          <div className="dashpg-flow-label">
            {ICONS.arrowDown}
            Today inflow
          </div>
          <div className={`dashpg-flow-val ${!todayIn ? "is-zero" : ""}`}>
            {symbol}
            {Number(todayIn || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="dashpg-flow-divider" />
        <div className="dashpg-flow-cell dashpg-flow-cell-out">
          <div className="dashpg-flow-label">
            {ICONS.arrowUp}
            Today outflow
          </div>
          <div className={`dashpg-flow-val ${!todayOut ? "is-zero" : ""}`}>
            {symbol}
            {Number(todayOut || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        </div>
      </div>

      {account?.description && <p className="dashpg-card-desc">{account.description}</p>}

      <div className="dashpg-card-spacer" />

      {chips.length > 0 && <div className="dashpg-card-chips">{chips}</div>}
    </div>
  );
}

function SortableCard({ account, onClick, currency, todayIn, todayOut }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: account._id,
  });

  const style = {
    transform: DndCSS.Translate.toString(transform),
    transition: transition || "transform 200ms ease",
    visibility: isDragging ? "hidden" : "visible",
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <AccountCardInner
        account={account}
        onClick={onClick}
        currency={currency}
        isDragging={isDragging}
        todayIn={todayIn}
        todayOut={todayOut}
      />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────────────
export default function DashboardPage() {
  const navigate = useNavigate();

  // ─── State preserved from the original implementation ──────────────
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("USD");

  // DnD overlay
  const [activeId, setActiveId] = useState(null);
  const [activeAccountObj, setActiveAccountObj] = useState(null);

  // Quick transaction modal
  const [openModal, setOpenModal] = useState(false);
  const [activeAccount, setActiveAccount] = useState(null);
  const [todayInflow, setTodayInflow] = useState(0);
  const [todayOutflow, setTodayOutflow] = useState(0);
  const [txnType, setTxnType] = useState("Receipt");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [transferTarget, setTransferTarget] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [txnMessage, setTxnMessage] = useState("");
  const [txnMessageKind, setTxnMessageKind] = useState("info");
  const [txnLoading, setTxnLoading] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState(null);
  const [otherAccounts, setOtherAccounts] = useState([]);

  // Per-account today flows (new — to power the in-card inflow/outflow row).
  // Map: { [accountId]: { in: number, out: number } }
  const [accountFlows, setAccountFlows] = useState({});

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const todayIsoRef = useRef(new Date().toISOString().split("T")[0]);

  // ─── Data fetchers ─────────────────────────────────────────────────
  const fetchUserCurrency = useCallback(async () => {
    try {
      const res = await API.get("/users/me");
      setCurrency(res.data.currency || "USD");
    } catch (err) {
      console.error("Error fetching user currency:", err);
    }
  }, []);

  /** Single org-wide /transactions call for today, then aggregate per-account
   *  in/out totals for the card flow row. */
  const fetchAccountFlows = useCallback(async () => {
    try {
      const today = todayIsoRef.current;
      const res = await API.get("/transactions", {
        params: { startDate: today, endDate: today, limit: 100000 },
      });
      const txns = res.data.transactions || [];
      const map = {};
      const ensure = (id) => {
        if (!map[id]) map[id] = { in: 0, out: 0 };
        return map[id];
      };
      txns.forEach((t) => {
        const amt = Number(t.amount || 0);
        if (t.type === "Receipt" && t.account?._id) {
          ensure(t.account._id).in += amt;
        } else if (t.type === "Payment" && t.account?._id) {
          ensure(t.account._id).out += amt;
        } else if (t.type === "Transfer") {
          const fromId = t.fromAccount?._id || t.fromAccount;
          const toId = t.toAccount?._id || t.toAccount;
          if (fromId) ensure(fromId).out += amt;
          if (toId) ensure(toId).in += amt;
        }
      });
      setAccountFlows(map);
    } catch (err) {
      console.error("Error fetching account flows:", err);
      setAccountFlows({});
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const accRes = await API.get("/accounts?search=&limit=100000000000");
      let arr = accRes.data.accounts || [];
      arr = arr.filter((a) => a.isActive);

      // Apply saved order (per-user, browser-local)
      const savedOrder = localStorage.getItem(STORAGE_KEY);
      if (savedOrder) {
        try {
          const savedIds = JSON.parse(savedOrder);
          const ordered = [];
          savedIds.forEach((id) => {
            const found = arr.find((a) => a._id === id);
            if (found) ordered.push(found);
          });
          const leftover = arr.filter((a) => !savedIds.includes(a._id));
          setAccounts([...ordered, ...leftover]);
        } catch {
          setAccounts(arr);
        }
      } else {
        setAccounts(arr);
      }

      const catRes = await API.get("/categories?limit=100000000");
      setCategories(catRes.data || []);
    } catch (err) {
      console.error("Error loading dashboard data:", err);
      setSnackbar({ open: true, message: "Failed to load dashboard data.", severity: "error" });
    }
  }, []);

  // Initial load — parallel for fastest paint
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await Promise.all([fetchData(), fetchUserCurrency(), fetchAccountFlows()]);
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [fetchData, fetchUserCurrency, fetchAccountFlows]);

  // ─── DnD handlers ──────────────────────────────────────────────────
  const handleDragStart = (event) => {
    const { active } = event;
    setActiveId(active.id);
    const acct = accounts.find((a) => a._id === active.id);
    setActiveAccountObj(acct);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setActiveAccountObj(null);
    if (!over || active.id === over.id) return;

    const oldIndex = accounts.findIndex((a) => a._id === active.id);
    const newIndex = accounts.findIndex((a) => a._id === over.id);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;

    const updated = arrayMove(accounts, oldIndex, newIndex);
    setAccounts(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated.map((a) => a._id)));
  };

  // ─── Card click → open quick transaction modal ─────────────────────
  const handleCardClick = (acc) => {
    setActiveAccount(acc);
    setTxnType("Receipt");
    setAmount("");
    setDescription("");
    setTransferTarget("");
    setCategoryId("");
    setAttachmentFile(null);
    setTxnMessage("");
    setTxnMessageKind("info");

    const others = accounts.filter((a) => a._id !== acc._id);
    setOtherAccounts(others);

    fetchTodayTotals(acc._id);
    setOpenModal(true);
  };

  async function fetchTodayTotals(accountId) {
    try {
      const today = todayIsoRef.current;
      const res = await API.get("/transactions", {
        params: { accountId, startDate: today, endDate: today, limit: 100000 },
      });
      const sameDay = res.data.transactions || [];
      let inflow = 0;
      let outflow = 0;
      sameDay.forEach((t) => {
        if (t.type === "Receipt") inflow += Number(t.amount || 0);
        else if (t.type === "Payment") outflow += Number(t.amount || 0);
        else if (t.type === "Transfer") {
          const fromId = String(t.fromAccount?._id || t.fromAccount);
          const toId = String(t.toAccount?._id || t.toAccount);
          if (fromId === accountId) outflow += Number(t.amount || 0);
          if (toId === accountId) inflow += Number(t.amount || 0);
        }
      });
      setTodayInflow(inflow);
      setTodayOutflow(outflow);
    } catch (err) {
      console.error("Error fetchTodayTotals:", err);
      setTodayInflow(0);
      setTodayOutflow(0);
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false);
    setActiveAccount(null);
    setTxnMessage("");
    setTxnLoading(false);
  };

  // ─── Save transaction ──────────────────────────────────────────────
  const handleAddTransaction = async () => {
    setTxnMessage("");
    setTxnMessageKind("info");

    if (!categoryId) {
      setTxnMessage("Please choose a category.");
      setTxnMessageKind("err");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setTxnMessage("Amount must be greater than 0.");
      setTxnMessageKind("err");
      return;
    }
    if (txnType === "Transfer" && !transferTarget) {
      setTxnMessage("Please choose a destination account for the transfer.");
      setTxnMessageKind("err");
      return;
    }

    setTxnLoading(true);
    try {
      const today = todayIsoRef.current;
      const formData = new FormData();
      formData.append("date", today);
      formData.append("description", description);
      formData.append("amount", Number(amount));
      formData.append("category", categoryId);
      formData.append("type", txnType);

      if (txnType === "Receipt" || txnType === "Payment") {
        formData.append("account", activeAccount._id);
      } else if (txnType === "Transfer") {
        formData.append("fromAccount", activeAccount._id);
        formData.append("toAccount", transferTarget);
      }

      if (attachmentFile) {
        formData.append("attachment", attachmentFile);
      }

      await API.post("/transactions/create", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setTxnMessage(`${txnType} of ${formatMoney(Number(amount), currency)} recorded on ${activeAccount.accountName}.`);
      setTxnMessageKind("ok");
      setAmount("");
      setDescription("");
      setTransferTarget("");
      setCategoryId("");
      setAttachmentFile(null);

      // Refresh dashboard balances + per-account flows + this account's totals
      await Promise.all([fetchData(), fetchAccountFlows()]);
      if (activeAccount) await fetchTodayTotals(activeAccount._id);
    } catch (err) {
      console.error("Transaction error:", err);
      const errorData = err.response?.data;
      let message = errorData?.message || err.message || "Failed to save transaction.";
      if (errorData?.locked) {
        message = `🔒 ${errorData.message}`;
        setTxnMessageKind("lock");
      } else {
        setTxnMessageKind("err");
      }
      setTxnMessage(message);
    } finally {
      setTxnLoading(false);
    }
  };

  const handleOpenReport = () => {
    if (!activeAccount) return;
    handleCloseModal();
    navigate(`/detailed-report?accountId=${activeAccount._id}`);
  };

  // Currency symbol for modal amount input
  const currencySymbol = useMemo(() => {
    switch (currency) {
      case "USD": return "$";
      case "EUR": return "€";
      case "GBP": return "£";
      case "JPY": return "¥";
      case "BDT": return "৳";
      case "INR": return "₹";
      default: return currency || "";
    }
  }, [currency]);

  // Categories filtered by selected type, sorted
  const typeCategoryOptions = useMemo(
    () =>
      categories
        .filter((c) => c.type === txnType)
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name)),
    [categories, txnType]
  );

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((p) => ({ ...p, open: false }));
  };

  const t = txnType.toLowerCase();
  const alerts = useMemo(() => alertCount(accounts), [accounts]);

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <>
      <style>{PAGE_CSS}</style>
      <div className="dashpg-root">
        <main className="dashpg-page">
          {/* Header */}
          <header className="dashpg-header">
            <div>
              <div className="dashpg-eyebrow">
                {ICONS.pulse}
                Live · {fmtLiveDate()}
              </div>
              <h1 className="dashpg-title">Dashboard.</h1>
              <p className="dashpg-sub">
                <strong>{accounts.length} {accounts.length === 1 ? "account" : "accounts"}</strong>
                <span className="dot" />
                <span>Cash Pro</span>
                {alerts > 0 && (
                  <>
                    <span className="dot" />
                    <span>{alerts} alert{alerts === 1 ? "" : "s"} to review</span>
                  </>
                )}
              </p>
            </div>
            <div className="dashpg-meta">
              {alerts > 0 && (
                <span className="dashpg-meta-chip dashpg-meta-chip-warn">
                  {ICONS.alert}
                  {alerts} alert{alerts === 1 ? "" : "s"} to review
                </span>
              )}
              {accounts.length > 1 && (
                <span className="dashpg-meta-chip">Drag to reorder · saved locally</span>
              )}
            </div>
          </header>

          {/* Loading */}
          {loading && (
            <div className="dashpg-state">
              <div className="dashpg-spinner-lg" />
              <div>Loading your accounts…</div>
            </div>
          )}

          {/* Empty */}
          {!loading && accounts.length === 0 && (
            <div className="dashpg-state">
              <div className="dashpg-state-illu">{ICONS.coin}</div>
              <div className="dashpg-state-title">No accounts assigned to you yet</div>
              <div className="dashpg-state-text">
                Contact your administrator to get access to one or more accounts in this organisation.
              </div>
            </div>
          )}

          {/* Account grid */}
          {!loading && accounts.length > 0 && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={accounts.map((a) => a._id)}
                strategy={rectSortingStrategy}
              >
                <div className="dashpg-grid">
                  {accounts.map((acc) => {
                    const flow = accountFlows[acc._id] || { in: 0, out: 0 };
                    return (
                      <SortableCard
                        key={acc._id}
                        account={acc}
                        onClick={() => handleCardClick(acc)}
                        currency={currency}
                        todayIn={flow.in}
                        todayOut={flow.out}
                      />
                    );
                  })}
                </div>
              </SortableContext>
              <DragOverlay>
                {activeAccountObj ? (
                  <AccountCardInner
                    account={activeAccountObj}
                    currency={currency}
                    todayIn={(accountFlows[activeAccountObj._id] || {}).in || 0}
                    todayOut={(accountFlows[activeAccountObj._id] || {}).out || 0}
                    asOverlay
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </main>

        {/* ─── Quick Transaction Modal ─── */}
        <Dialog
          open={openModal}
          onClose={handleCloseModal}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            className: "dashpg-modal-root",
            style: {
              borderRadius: 16,
              boxShadow: "0 24px 60px rgba(11,12,14,0.2)",
              border: "1px solid #ECECEF",
              overflow: "hidden",
            },
          }}
          BackdropProps={{
            style: { background: "rgba(11, 12, 14, 0.42)", backdropFilter: "blur(3px)" },
          }}
        >
          {activeAccount && (
            <>
              <div className="dashpg-modal-head">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="dashpg-modal-eyebrow">Quick Transaction</div>
                  <h2 className="dashpg-modal-title">{activeAccount.accountName}</h2>
                  <p className="dashpg-modal-sub">
                    #{activeAccount.accountNo} &nbsp;·&nbsp; Balance{" "}
                    {formatMoney(activeAccount.balance || 0, currency)}
                  </p>
                </div>
                <button className="dashpg-modal-close" onClick={handleCloseModal} aria-label="Close">
                  {ICONS.close}
                </button>
              </div>

              <div className="dashpg-modal-body">
                {/* Today totals */}
                <div className="dashpg-stats">
                  <div className="dashpg-stat dashpg-stat-in">
                    <div className="dashpg-stat-label">
                      {ICONS.arrowDown}
                      Today Inflow
                    </div>
                    <div className="dashpg-stat-val">
                      {formatMoney(todayInflow, currency)}
                    </div>
                  </div>
                  <div className="dashpg-stat dashpg-stat-out">
                    <div className="dashpg-stat-label">
                      {ICONS.arrowUp}
                      Today Outflow
                    </div>
                    <div className="dashpg-stat-val">
                      {formatMoney(todayOutflow, currency)}
                    </div>
                  </div>
                </div>

                {/* Type selector */}
                <div className="dashpg-types">
                  <button
                    type="button"
                    data-type="receipt"
                    className={`dashpg-type-btn ${txnType === "Receipt" ? "is-active" : ""}`}
                    onClick={() => setTxnType("Receipt")}
                  >
                    {ICONS.coin}
                    Receipt
                  </button>
                  <button
                    type="button"
                    data-type="payment"
                    className={`dashpg-type-btn ${txnType === "Payment" ? "is-active" : ""}`}
                    onClick={() => setTxnType("Payment")}
                  >
                    {ICONS.card}
                    Payment
                  </button>
                  <button
                    type="button"
                    data-type="transfer"
                    className={`dashpg-type-btn ${txnType === "Transfer" ? "is-active" : ""}`}
                    onClick={() => setTxnType("Transfer")}
                  >
                    {ICONS.swap}
                    Transfer
                  </button>
                  <button
                    type="button"
                    data-type="report"
                    className="dashpg-type-btn"
                    onClick={handleOpenReport}
                  >
                    {ICONS.list}
                    Report
                  </button>
                </div>

                {/* Feedback */}
                {txnMessage && (
                  <div
                    className={`dashpg-alert ${
                      txnMessageKind === "ok"
                        ? "dashpg-alert-ok"
                        : txnMessageKind === "lock"
                        ? "dashpg-alert-lock"
                        : "dashpg-alert-err"
                    }`}
                  >
                    {txnMessageKind === "ok" ? ICONS.check : ICONS.alert}
                    <span>{txnMessage}</span>
                  </div>
                )}

                {/* Form */}
                <div className="dashpg-field">
                  <label className="dashpg-field-label">
                    <span>Category</span>
                    <span className="opt">Required</span>
                  </label>
                  <Autocomplete
                    size="small"
                    options={typeCategoryOptions}
                    getOptionLabel={(opt) => opt.name || ""}
                    value={typeCategoryOptions.find((c) => c._id === categoryId) || null}
                    isOptionEqualToValue={(o, v) => o._id === v._id}
                    onChange={(_, v) => setCategoryId(v?._id || "")}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={`Search ${t} categories…`}
                        variant="outlined"
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "10px",
                            fontSize: 14,
                            fontFamily: "'Geist', sans-serif",
                          },
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: "#ECECEF" },
                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#DEDFE3" },
                          "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#0B0C0E !important", borderWidth: "1px !important" },
                        }}
                      />
                    )}
                    ListboxProps={{ style: { maxHeight: 220, fontFamily: "'Geist', sans-serif", fontSize: 13.5 } }}
                  />
                </div>

                <div className="dashpg-field">
                  <label className="dashpg-field-label">
                    <span>Amount</span>
                    <span className="opt">{currency}</span>
                  </label>
                  <div className="dashpg-amount-wrap">
                    <span className="ccy">{currencySymbol}</span>
                    <input
                      className="dashpg-modal-input dashpg-amount-input"
                      type="number"
                      step="0.01"
                      min="0.01"
                      inputMode="decimal"
                      placeholder="0.00"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      onWheel={(e) => e.target.blur()}
                    />
                  </div>
                </div>

                {txnType === "Transfer" && (
                  <div className="dashpg-field">
                    <label className="dashpg-field-label">
                      <span>Transfer to</span>
                      <span className="opt">Required</span>
                    </label>
                    <select
                      className="dashpg-modal-select"
                      value={transferTarget}
                      onChange={(e) => setTransferTarget(e.target.value)}
                    >
                      <option value="">Select destination account…</option>
                      {otherAccounts.filter((a) => a.isActive).map((a) => (
                        <option key={a._id} value={a._id}>
                          {a.accountName} (#{a.accountNo})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="dashpg-field">
                  <label className="dashpg-field-label">
                    <span>Description</span>
                    <span className="opt">Optional</span>
                  </label>
                  <input
                    className="dashpg-modal-input"
                    type="text"
                    placeholder={
                      txnType === "Receipt"
                        ? "e.g. Saturday market till takings"
                        : txnType === "Payment"
                        ? "e.g. Supplier invoice — Acme Ltd"
                        : "e.g. Cash-up to operating account"
                    }
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="dashpg-field">
                  <label className="dashpg-field-label">
                    <span>Attachment</span>
                    <span className="opt">Optional</span>
                  </label>
                  <label className="dashpg-upload">
                    {ICONS.paperclip}
                    <span>{attachmentFile ? "Replace attachment" : "Upload receipt photo or document"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)}
                    />
                  </label>
                  {attachmentFile && (
                    <div className="dashpg-upload-file">
                      {ICONS.check}
                      {attachmentFile.name}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="dashpg-save-btn"
                  data-type={t}
                  onClick={handleAddTransaction}
                  disabled={txnLoading}
                >
                  {txnLoading && <span className="dashpg-modal-spinner" />}
                  {txnLoading ? "Saving…" : `Save ${txnType}`}
                </button>
              </div>
            </>
          )}
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            variant="standard"
            sx={{
              minWidth: 320,
              borderRadius: 2,
              fontFamily: "'Geist', sans-serif",
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
}
